import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";

// Exact 4 options shown to clients, in this order.
const BOOKABLE_NAMES = [
  "Allergy Panel",
  "Allergy Routine Appt",
  "Emotion / Body Code",
  "Bio-Frequency Scan",
];

const COPY = {
  "Allergy Panel": "Comprehensive intake plus our full allergen panel. Plan for the longer first visit.",
  "Allergy Routine Appt": "Follow-up allergen clearing. Routine visits move quickly.",
  "Emotion / Body Code": "Trapped-emotion release with Jennie, Kendra, or Jaylin. Remote or in-person.",
  "Bio-Frequency Scan": "A full reading of your body's subtle energy fields — no guesswork.",
};

export default function BookingChooser() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/services", { params: { only_active: true } })
      .then(({ data }) => setServices(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const options = BOOKABLE_NAMES
    .map((name) => services.find((s) => s.name === name))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-tf-cream-50 text-tf-ink font-ui">
      {/* Top bar */}
      <header className="border-b border-tf-ink/10 bg-tf-cream-50/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-tf-ink-soft hover:text-tf-green-800 transition" data-testid="book-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <Link to="/" className="font-serif text-xl text-tf-ink tracking-tight">The Root Forum</Link>
          <a href="tel:+16123850045" className="hidden sm:inline text-sm text-tf-ink-soft hover:text-tf-green-800">612-385-0045</a>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-tf-green-700 mb-3">— Step 1 of 4 · Choose your visit</p>
          <h1 className="font-serif text-[clamp(2.4rem,4.2vw,3.6rem)] leading-[1.05] tracking-tight mb-5">
            What kind of visit <span className="italic font-light text-tf-green-800">would you like?</span>
          </h1>
          <p className="text-tf-ink-soft text-base leading-relaxed mb-10">
            Pick the option that matches your needs. You'll choose a day and time on the next screen.
          </p>
        </div>

        {loading && (
          <div className="text-sm text-tf-ink-soft" data-testid="book-loading">Loading visit options…</div>
        )}

        {!loading && options.length === 0 && (
          <div className="text-sm text-tf-ink-soft" data-testid="book-empty">No bookable services found. Please call us at 612-385-0045.</div>
        )}

        <div className="grid sm:grid-cols-2 gap-5" data-testid="booking-chooser-grid">
          {options.map((s, i) => (
            <Link
              key={s.id}
              to={`/book/${s.id}`}
              className="group relative bg-white border border-tf-ink/10 rounded-2xl p-7 hover:border-tf-green-700 hover:shadow-[0_8px_30px_-12px_rgba(42,74,55,0.25)] transition-all duration-300"
              data-testid={`book-option-${i}`}
            >
              <div className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-tf-green-700 mb-3">
                0{i + 1} / Visit
              </div>
              <h2 className="font-serif text-2xl text-tf-ink leading-tight mb-3">{s.name}</h2>
              <div className="inline-flex items-center gap-1.5 text-xs font-mono text-tf-ink-soft tracking-wider mb-4">
                <Clock className="w-3.5 h-3.5" />
                {s.duration_minutes} min
              </div>
              <p className="text-tf-ink-soft text-sm leading-relaxed mb-6">
                {COPY[s.name] || s.description}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-tf-green-800 border-b border-tf-green-800 pb-0.5 group-hover:gap-2.5 transition-all">
                Choose this visit <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>

        <p className="text-xs text-tf-ink-soft mt-12 italic">
          Not sure which to pick? Call us at <a href="tel:+16123850045" className="underline hover:text-tf-green-800">612-385-0045</a> and we'll help.
        </p>
      </main>
    </div>
  );
}
